import React, { useState, useRef, useCallback } from 'react';

const BeforeAfter: React.FC = () => {
  const [sliderPos, setSliderPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const updateSlider = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const position = Math.min(Math.max((x / rect.width) * 100, 2), 98);
    setSliderPos(position);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    updateSlider(e.clientX);
  };

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    updateSlider(e.clientX);
  }, [isDragging, updateSlider]);

  const handleMouseUp = () => setIsDragging(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    updateSlider(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    updateSlider(e.touches[0].clientX);
  };

  const handleTouchEnd = () => setIsDragging(false);

  return (
    <section className="py-24 bg-brand-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-5xl font-extrabold text-brand-navy mb-6">
            See the <span className="text-brand-blue">Difference</span>
          </h2>
          <p className="text-lg text-brand-navy/70">
            Professional cleaning makes a world of difference. Drag the slider to see our results.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div
            ref={containerRef}
            className={`relative w-full aspect-[4/3] md:aspect-video rounded-[2rem] md:rounded-[2.5rem] overflow-hidden shadow-2xl border-4 md:border-8 border-white select-none ${isDragging ? 'cursor-ew-resize' : 'cursor-ew-resize'}`}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* AFTER Image (full background) */}
            <div className="absolute inset-0">
              <img
                src="/assets/img/after.jpg"
                alt="After Professional Cleaning"
                className="w-full h-full object-cover"
                draggable={false}
              />
              {/* After label */}
              <div className="absolute bottom-4 right-4 bg-brand-blue text-white px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest shadow-lg">
                After
              </div>
            </div>

            {/* BEFORE Image (clipped foreground) */}
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}
            >
              <img
                src="/assets/img/before.jpg"
                alt="Before Professional Cleaning"
                className="w-full h-full object-cover"
                draggable={false}
              />
              {/* Before label */}
              <div className="absolute bottom-4 left-4 bg-brand-navy text-white px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest shadow-lg">
                Before
              </div>
            </div>

            {/* Divider Line */}
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_12px_rgba(0,0,0,0.4)] z-20 pointer-events-none"
              style={{ left: `${sliderPos}%` }}
            />

            {/* Slider Handle */}
            <div
              className="absolute top-1/2 -translate-y-1/2 z-30 pointer-events-none"
              style={{ left: `${sliderPos}%`, transform: 'translate(-50%, -50%)' }}
            >
              <div className="w-12 h-12 md:w-14 md:h-14 bg-white rounded-full shadow-2xl flex items-center justify-center border-2 border-white/50">
                <svg className="w-6 h-6 text-brand-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 9l-3 3 3 3M16 9l3 3-3 3" />
                </svg>
              </div>
            </div>
          </div>

          <p className="text-center mt-6 text-sm font-bold text-brand-navy/40 uppercase tracking-widest">
            Drag slider to compare — Real results from our clients
          </p>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfter;
